// biome-ignore-all lint/suspicious/noExplicitAny: internal type-erased dispatch — plugins are registered with concrete module maps but stored erased by the orchestrator
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ModuleEventLevel } from '../../../types.js';
import { ModuleConfiguratorEventName } from '../module-configurator-event-name.js';
/**
 * Checks whether a plugin return value should be treated as dispose-time cleanup.
 *
 * Plugins may return either a plain callback or an object with a `dispose` method.
 * Any other value is ignored so plugin authors can return `undefined` when no
 * cleanup is needed.
 *
 * @param value - Value returned by a registered plugin callback.
 * @returns True when the value is a valid plugin teardown registration.
 */
function isPluginTeardown(value) {
    return (typeof value === 'function' ||
        (typeof value === 'object' &&
            value !== null &&
            'dispose' in value &&
            typeof value.dispose === 'function'));
}
/**
 * Runs configurator plugins after modules are ready.
 *
 * The plugin phase is the final initialization step before `initialize()`
 * resolves. Each plugin receives the sealed module instance map and optional
 * initialization reference, which makes this phase suitable for application
 * side effects such as telemetry bridges, global event listeners, and runtime
 * subscriptions that require multiple initialized module providers.
 *
 * Plugin callbacks run concurrently. Failures are isolated and emitted as
 * warning events so one failing side effect cannot block application rendering.
 * Returned teardown callbacks are stored for the dispose phase and are invoked
 * before module `dispose` hooks.
 *
 * @param ctx - The plugin phase context.
 * @param modules - The initialized module instance map.
 * @param ref - Optional reference forwarded from module initialization.
 * @returns A promise resolving when all plugin callbacks have settled.
 * @template TRef - Reference type forwarded to plugin callbacks.
 */
export async function runPluginPhase(ctx, modules, ref) {
    const { plugins, teardowns, registerEvent } = ctx;
    // Skip the plugin phase entirely when no plugins were registered
    if (!plugins.length)
        return;
    registerEvent({
        level: ModuleEventLevel.Debug,
        name: ModuleConfiguratorEventName.PluginsRegister,
        message: `Registering plugins [${plugins.length}]`,
        properties: { count: plugins.length },
    });
    const pluginRegistrations = await Promise.all(plugins
        // Run every plugin callback concurrently so one slow plugin cannot delay the others
        .map(async (plugin) => {
        const pluginStart = performance.now();
        const name = plugin.name || 'anonymous';
        try {
            const teardown = await plugin({ modules, ref });
            const pluginTime = Math.round(performance.now() - pluginStart);
            registerEvent({
                level: ModuleEventLevel.Debug,
                name: ModuleConfiguratorEventName.PluginRegistered,
                message: `Plugin ${name} registered in ${pluginTime}ms`,
                properties: { name, pluginTime },
                metric: pluginTime,
            });
            return isPluginTeardown(teardown) ? teardown : undefined;
        }
        catch (err) {
            registerEvent({
                level: ModuleEventLevel.Warning,
                name: ModuleConfiguratorEventName.PluginRegisterError,
                message: `Plugin ${name} registration failed`,
                properties: { name },
                error: err,
            });
            return undefined;
        }
    }));
    // Queue every plugin teardown returned above so the dispose phase can invoke them later
    for (const teardown of pluginRegistrations) {
        // Only queue teardowns for plugins that actually returned one
        if (teardown) {
            teardowns.push(teardown);
        }
    }
    registerEvent({
        level: ModuleEventLevel.Debug,
        name: ModuleConfiguratorEventName.PluginsRegistered,
        message: 'Plugin registration complete',
        properties: { count: plugins.length, teardowns: teardowns.length },
    });
}
//# sourceMappingURL=run-plugin-phase.js.map